import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  FileText,
  Plus,
  Search,
  Building2,
  Phone,
  Mail,
  MapPin,
  Loader2,
  Send,
  Download,
  Euro,
  Link2,
  RefreshCw,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  Copy,
  ExternalLink,
  Edit,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  en_attente: { label: "En attente", color: "bg-gray-100 text-gray-800", icon: Clock },
  envoyee: { label: "Envoyée", color: "bg-blue-100 text-blue-800", icon: Send },
  payee: { label: "Payée", color: "bg-green-100 text-green-800", icon: CheckCircle },
  annulee: { label: "Annulée", color: "bg-red-100 text-red-800", icon: XCircle },
};

export default function FacturationExternePage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false); // Mode édition

  // Autocomplete data
  const [autocompleteData, setAutocompleteData] = useState({ food_trucks: [], clients: [] });
  const [autocompleteSearch, setAutocompleteSearch] = useState("");
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [selectedType, setSelectedType] = useState(null); // "food_truck" or "client" or null

  // Form state
  const [form, setForm] = useState({
    nom_entreprise: "",
    nom_contact: "",
    email: "",
    telephone: "",
    adresse: "",
    ville: "",
    code_postal: "",
    siret: "",
    tva_intra: "",
    items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
    notes: "",
  });

  // Load autocomplete data
  const loadAutocompleteData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/external-invoices/autocomplete-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAutocompleteData(response.data);
    } catch (error) {
      console.error("Error loading autocomplete data:", error);
    }
  };

  // Load invoices
  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/external-invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Ensure invoices is always an array
      setInvoices(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error loading invoices:", error);
      toast.error("Erreur lors du chargement des factures");
      setInvoices([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    loadAutocompleteData();
  }, []);

  // Filter autocomplete options
  const filteredOptions = [...autocompleteData.food_trucks, ...autocompleteData.clients].filter(opt =>
    opt.label?.toLowerCase().includes(autocompleteSearch.toLowerCase()) ||
    opt.nom_entreprise?.toLowerCase().includes(autocompleteSearch.toLowerCase()) ||
    opt.nom_contact?.toLowerCase().includes(autocompleteSearch.toLowerCase())
  );

  // Select from autocomplete
  const handleSelectAutocomplete = (option) => {
    setForm({
      ...form,
      nom_entreprise: option.nom_entreprise || option.label || "",
      nom_contact: option.nom_contact || "",
      email: option.email || "",
      telephone: option.telephone || "",
      adresse: option.adresse || "",
      ville: option.ville || "",
      code_postal: option.code_postal || "",
      siret: option.siret || "",
      tva_intra: option.tva_intra || "",
    });
    setSelectedType(option.type);
    setAutocompleteSearch(option.label || option.nom_entreprise || "");
    setShowAutocomplete(false);
    toast.success(`${option.type === "food_truck" ? "Food Truck" : "Client"} sélectionné`);
  };

  // Reset form
  const resetForm = () => {
    setForm({
      nom_entreprise: "",
      nom_contact: "",
      email: "",
      telephone: "",
      adresse: "",
      ville: "",
      code_postal: "",
      siret: "",
      tva_intra: "",
      items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
      notes: "",
    });
    setAutocompleteSearch("");
    setSelectedType(null);
    setShowAutocomplete(false);
  };

  // Open create dialog with reset
  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  // Calculate total
  const calculateTotal = () => {
    return form.items.reduce((sum, item) => sum + (item.quantite * item.prix_unitaire), 0);
  };

  // Add item
  const addItem = () => {
    setForm({
      ...form,
      items: [...form.items, { description: "", quantite: 1, prix_unitaire: 0 }],
    });
  };

  // Remove item
  const removeItem = (index) => {
    if (form.items.length > 1) {
      setForm({
        ...form,
        items: form.items.filter((_, i) => i !== index),
      });
    }
  };

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = {
      ...newItems[index],
      [field]: field === "quantite" || field === "prix_unitaire" ? parseFloat(value) || 0 : value,
    };
    setForm({ ...form, items: newItems });
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!form.nom_entreprise) {
      toast.error("Le nom de l'entreprise est requis");
      return;
    }
    // Permettre les montants négatifs pour les avoirs
    if (form.items.some(item => !item.description || item.prix_unitaire === 0)) {
      toast.error("Veuillez remplir toutes les lignes de facturation (montant ≠ 0)");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/external-invoices/generate`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Facture créée avec succès");
      setShowCreateDialog(false);
      setForm({
        nom_entreprise: "",
        nom_contact: "",
        email: "",
        telephone: "",
        adresse: "",
        ville: "",
        items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
        notes: "",
      });
      
      // Reload invoices first
      await loadInvoices();
      
      // Show the created invoice details if available
      if (response.data?.invoice) {
        // Ensure items array is present for detail view
        const invoiceWithItems = {
          ...response.data.invoice,
          items: response.data.invoice.items || []
        };
        setSelectedInvoice(invoiceWithItems);
        setShowDetailDialog(true);
      }
    } catch (error) {
      console.error("Error creating invoice:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Erreur lors de la création";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Update existing invoice
  const handleUpdateInvoice = async () => {
    if (!form.nom_entreprise) {
      toast.error("Le nom de l'entreprise est requis");
      return;
    }
    if (form.items.some(item => !item.description || item.prix_unitaire === 0)) {
      toast.error("Veuillez remplir toutes les lignes de facturation (montant ≠ 0)");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/external-invoices/${selectedInvoice.id}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Facture mise à jour");
      setShowCreateDialog(false);
      setEditMode(false);
      setSelectedInvoice(null);
      setForm({
        nom_entreprise: "",
        nom_contact: "",
        email: "",
        telephone: "",
        adresse: "",
        ville: "",
        items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
        notes: "",
      });
      
      loadInvoices();
    } catch (error) {
      console.error("Error updating invoice:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Erreur lors de la mise à jour";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (invoice) => {
    setForm({
      nom_entreprise: invoice.client_nom || "",
      nom_contact: invoice.client_contact || "",
      email: invoice.client_email || "",
      telephone: invoice.client_telephone || "",
      adresse: invoice.client_adresse || "",
      ville: invoice.client_ville || "",
      items: invoice.items?.length > 0 
        ? invoice.items.map(item => ({
            description: item.description || "",
            quantite: item.quantite || 1,
            prix_unitaire: item.prix_unitaire || 0
          }))
        : [{ description: "", quantite: 1, prix_unitaire: 0 }],
      notes: invoice.notes || "",
    });
    setSelectedInvoice(invoice);
    setEditMode(true);
    setShowCreateDialog(true);
  };

  // Send invoice
  const handleSendInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/external-invoices/${invoiceId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Facture envoyée par email");
      loadInvoices();
    } catch (error) {
      console.error("Error sending invoice:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  // Download PDF
  const handleDownloadPdf = async (invoice) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/external-invoices/${invoice.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `facture_${invoice.numero}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  // Regenerate payment link
  const handleRegeneratePaymentLink = async (invoiceId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/external-invoices/${invoiceId}/regenerate-payment-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Lien de paiement régénéré");
      loadInvoices();
      
      // Update selected invoice if viewing
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice({
          ...selectedInvoice,
          stripe_payment_url: response.data.payment_url,
        });
      }
    } catch (error) {
      console.error("Error regenerating payment link:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la régénération");
    }
  };

  // Mark as paid
  const handleMarkAsPaid = async (invoiceId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/external-invoices/${invoiceId}/status?new_status=payee`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Facture marquée comme payée");
      loadInvoices();
      setShowDetailDialog(false);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // Copy payment link
  const copyPaymentLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Lien copié dans le presse-papiers");
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice =>
    invoice.client_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.numero?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const stats = {
    total: invoices.length,
    enAttente: invoices.filter(i => i.statut === "en_attente").length,
    envoyees: invoices.filter(i => i.statut === "envoyee").length,
    payees: invoices.filter(i => i.statut === "payee").length,
    totalMontant: invoices.filter(i => i.statut === "payee").reduce((sum, i) => sum + (i.total_ttc || 0), 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="facturation-externe-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Facturation Externe
          </h1>
          <p className="text-muted-foreground text-sm">
            Générez des factures avec lien de paiement Stripe pour les non-membres
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Facture
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-secondary">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Total factures</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-gray-600">{stats.enAttente}</div>
            <p className="text-xs text-muted-foreground">En attente</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{stats.envoyees}</div>
            <p className="text-xs text-muted-foreground">Envoyées</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">{stats.payees}</div>
            <p className="text-xs text-muted-foreground">Payées</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-primary">{stats.totalMontant.toFixed(2)} €</div>
            <p className="text-xs text-muted-foreground">Encaissé</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Rechercher par client ou numéro..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N° Facture</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Paiement</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Aucune facture trouvée
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => {
                  const StatusIcon = STATUS_LABELS[invoice.statut]?.icon || Clock;
                  return (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.numero}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{invoice.client_nom}</div>
                          {invoice.client_email && (
                            <div className="text-xs text-muted-foreground">{invoice.client_email}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">{invoice.total_ttc?.toFixed(2)} €</TableCell>
                      <TableCell className="text-sm">{invoice.date_emission}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_LABELS[invoice.statut]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_LABELS[invoice.statut]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {invoice.stripe_payment_url && invoice.statut !== "payee" ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyPaymentLink(invoice.stripe_payment_url)}
                              title="Copier le lien"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(invoice.stripe_payment_url, "_blank")}
                              title="Ouvrir le lien"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : invoice.statut === "payee" ? (
                          <span className="text-green-600 text-sm flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Payée
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPdf(invoice)}
                            title="Télécharger PDF"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {invoice.client_email && invoice.statut !== "payee" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendInvoice(invoice.id)}
                              title="Envoyer par email"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedInvoice({...invoice, items: invoice.items || []});
                              setShowDetailDialog(true);
                            }}
                            title="Voir détails"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) {
          setEditMode(false);
          setSelectedInvoice(null);
          setForm({
            nom_entreprise: "",
            nom_contact: "",
            email: "",
            telephone: "",
            adresse: "",
            ville: "",
            items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
            notes: "",
          });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {editMode ? `Modifier la Facture ${selectedInvoice?.numero || ''}` : "Nouvelle Facture"}
            </DialogTitle>
            <DialogDescription>
              {editMode 
                ? "Modifiez les informations de cette facture. Les montants négatifs créent un avoir."
                : "Créez une facture pour un client externe avec lien de paiement Stripe. Les montants négatifs créent un avoir."
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Client Search/Autocomplete */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Sélectionner ou Saisir un Client
              </h3>
              
              {/* Autocomplete Input */}
              <div className="relative">
                <Label htmlFor="client_search">Rechercher un Food Truck ou Client existant</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="client_search"
                    value={autocompleteSearch}
                    onChange={(e) => {
                      setAutocompleteSearch(e.target.value);
                      setShowAutocomplete(true);
                      // If typing manually, update nom_entreprise too
                      if (!filteredOptions.some(opt => opt.label === e.target.value)) {
                        setForm({ ...form, nom_entreprise: e.target.value });
                        setSelectedType(null);
                      }
                    }}
                    onFocus={() => setShowAutocomplete(true)}
                    placeholder="Tapez pour rechercher ou saisir un nouveau nom..."
                    className="pl-10"
                  />
                </div>
                
                {/* Autocomplete Dropdown */}
                {showAutocomplete && autocompleteSearch && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredOptions.length > 0 ? (
                      <>
                        {/* Food Trucks */}
                        {filteredOptions.filter(o => o.type === "food_truck").length > 0 && (
                          <div className="px-2 py-1 bg-amber-50 text-amber-700 text-xs font-semibold">
                            Food Trucks
                          </div>
                        )}
                        {filteredOptions.filter(o => o.type === "food_truck").map((opt, idx) => (
                          <div
                            key={`ft-${idx}`}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => handleSelectAutocomplete(opt)}
                          >
                            <Building2 className="w-4 h-4 text-amber-600" />
                            <div>
                              <div className="font-medium">{opt.label}</div>
                              {opt.email && <div className="text-xs text-gray-500">{opt.email}</div>}
                            </div>
                          </div>
                        ))}
                        
                        {/* Clients */}
                        {filteredOptions.filter(o => o.type === "client").length > 0 && (
                          <div className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold">
                            Clients
                          </div>
                        )}
                        {filteredOptions.filter(o => o.type === "client").map((opt, idx) => (
                          <div
                            key={`cl-${idx}`}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                            onClick={() => handleSelectAutocomplete(opt)}
                          >
                            <Building2 className="w-4 h-4 text-blue-600" />
                            <div>
                              <div className="font-medium">{opt.label}</div>
                              {opt.nom_contact && <div className="text-xs text-gray-500">{opt.nom_contact}</div>}
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        Aucun résultat - vous pouvez saisir un nouveau client
                      </div>
                    )}
                    
                    {/* Manual entry option */}
                    <div 
                      className="px-3 py-2 border-t bg-gray-50 hover:bg-gray-100 cursor-pointer flex items-center gap-2 text-gray-600"
                      onClick={() => {
                        setForm({ ...form, nom_entreprise: autocompleteSearch });
                        setSelectedType(null);
                        setShowAutocomplete(false);
                      }}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Utiliser "{autocompleteSearch}" comme nouveau client</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Selected indicator */}
              {selectedType && (
                <div className="flex items-center gap-2 text-sm">
                  <Badge className={selectedType === "food_truck" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}>
                    {selectedType === "food_truck" ? "Food Truck" : "Client"} sélectionné
                  </Badge>
                  <span className="text-gray-600">Les informations ont été pré-remplies</span>
                </div>
              )}
            </div>

            {/* Client Info Form */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-gray-700">
                Informations Client
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="nom_entreprise">Nom de l'entreprise *</Label>
                  <Input
                    id="nom_entreprise"
                    value={form.nom_entreprise}
                    onChange={(e) => setForm({ ...form, nom_entreprise: e.target.value })}
                    placeholder="Nom de l'entreprise ou du client"
                  />
                </div>
                <div>
                  <Label htmlFor="nom_contact">Contact</Label>
                  <Input
                    id="nom_contact"
                    value={form.nom_contact}
                    onChange={(e) => setForm({ ...form, nom_contact: e.target.value })}
                    placeholder="Nom du contact"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <Label htmlFor="telephone">Téléphone</Label>
                  <Input
                    id="telephone"
                    value={form.telephone}
                    onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    placeholder="01 23 45 67 89"
                  />
                </div>
                <div>
                  <Label htmlFor="ville">Ville</Label>
                  <Input
                    id="ville"
                    value={form.ville}
                    onChange={(e) => setForm({ ...form, ville: e.target.value })}
                    placeholder="Paris"
                  />
                </div>
                <div>
                  <Label htmlFor="code_postal">Code Postal</Label>
                  <Input
                    id="code_postal"
                    value={form.code_postal}
                    onChange={(e) => setForm({ ...form, code_postal: e.target.value })}
                    placeholder="75001"
                  />
                </div>
                <div>
                  <Label htmlFor="siret">SIRET</Label>
                  <Input
                    id="siret"
                    value={form.siret}
                    onChange={(e) => setForm({ ...form, siret: e.target.value })}
                    placeholder="123 456 789 00012"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="adresse">Adresse</Label>
                  <Input
                    id="adresse"
                    value={form.adresse}
                    onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Lignes de facturation
                </h3>
                <Button variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              
              <div className="space-y-3">
                {form.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      {index === 0 && <Label className="text-xs">Description</Label>}
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                        placeholder="Description de la prestation"
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-xs">Qté</Label>}
                      <Input
                        type="number"
                        min="1"
                        value={item.quantite}
                        onChange={(e) => updateItem(index, "quantite", e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                      {index === 0 && <Label className="text-xs">Prix unit. (€)</Label>}
                      <Input
                        type="number"
                        step="0.01"
                        value={item.prix_unitaire}
                        onChange={(e) => updateItem(index, "prix_unitaire", e.target.value)}
                        className={item.prix_unitaire < 0 ? "border-red-300 text-red-600" : ""}
                      />
                    </div>
                    <div className="col-span-1">
                      {form.items.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex justify-end border-t pt-4">
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">Total HT</div>
                  <div className="text-2xl font-bold text-primary">{calculateTotal().toFixed(2)} €</div>
                  <div className="text-xs text-muted-foreground">TVA non applicable – article 293 B du CGI</div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes (optionnel)</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Notes additionnelles..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={editMode ? handleUpdateInvoice : handleCreateInvoice} 
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              {editMode ? "Mettre à jour" : "Créer la facture"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Facture {selectedInvoice?.numero}
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-4 py-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Statut</span>
                <Badge className={STATUS_LABELS[selectedInvoice.statut]?.color}>
                  {STATUS_LABELS[selectedInvoice.statut]?.label}
                </Badge>
              </div>

              {/* Client */}
              <div className="space-y-2">
                <h4 className="font-semibold">Client</h4>
                <div className="bg-muted/50 p-3 rounded-lg text-sm">
                  <p className="font-medium">{selectedInvoice.client_nom}</p>
                  {selectedInvoice.client_contact && <p>{selectedInvoice.client_contact}</p>}
                  {selectedInvoice.client_email && (
                    <p className="flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {selectedInvoice.client_email}
                    </p>
                  )}
                  {selectedInvoice.client_telephone && (
                    <p className="flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {selectedInvoice.client_telephone}
                    </p>
                  )}
                </div>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <h4 className="font-semibold">Lignes de facturation</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2">Description</th>
                        <th className="text-center p-2">Qté</th>
                        <th className="text-right p-2">Prix unit.</th>
                        <th className="text-right p-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items?.map((item, idx) => (
                        <tr key={idx} className={`border-t ${item.prix_unitaire < 0 ? 'bg-red-50' : ''}`}>
                          <td className="p-2">{item.description}</td>
                          <td className="text-center p-2">{item.quantite}</td>
                          <td className={`text-right p-2 ${item.prix_unitaire < 0 ? 'text-red-600' : ''}`}>
                            {item.prix_unitaire?.toFixed(2)} €
                          </td>
                          <td className={`text-right p-2 font-medium ${item.total < 0 ? 'text-red-600' : ''}`}>
                            {item.total?.toFixed(2)} €
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Totals */}
                <div className="bg-muted/30 p-3 rounded-lg space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Total HT</span>
                    <span>{selectedInvoice.total_ht?.toFixed(2)} €</span>
                  </div>
                  {selectedInvoice.tva_rate > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>TVA ({selectedInvoice.tva_rate}%)</span>
                      <span>{selectedInvoice.tva_amount?.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Total TTC</span>
                    <span className={selectedInvoice.total_ttc < 0 ? 'text-red-600' : 'text-primary'}>
                      {selectedInvoice.total_ttc?.toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Link */}
              {selectedInvoice.stripe_payment_url && selectedInvoice.statut !== "payee" && (
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Lien de paiement Stripe
                  </h4>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Input
                        value={selectedInvoice.stripe_payment_url}
                        readOnly
                        className="text-xs bg-white"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyPaymentLink(selectedInvoice.stripe_payment_url)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(selectedInvoice.stripe_payment_url, "_blank")}
                        className="flex-1"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Ouvrir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRegeneratePaymentLink(selectedInvoice.id)}
                        className="flex-1"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Régénérer
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Date émission</span>
                  <p className="font-medium">{selectedInvoice.date_emission}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Date échéance</span>
                  <p className="font-medium">{selectedInvoice.date_echeance}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                {selectedInvoice.statut !== "payee" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      openEditDialog(selectedInvoice);
                      setShowDetailDialog(false);
                    }}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => handleDownloadPdf(selectedInvoice)}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-1" />
                  PDF
                </Button>
                {selectedInvoice.client_email && selectedInvoice.statut !== "payee" && (
                  <Button
                    variant="outline"
                    onClick={() => handleSendInvoice(selectedInvoice.id)}
                    className="flex-1"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Envoyer
                  </Button>
                )}
                {selectedInvoice.statut !== "payee" && (
                  <Button
                    onClick={() => handleMarkAsPaid(selectedInvoice.id)}
                    className="flex-1"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Marquer payée
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
